import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import React from 'react'
import { MissingContent } from '../components/MissingContent'
import {
	ContentFragment,
	ProductFragment,
	ProductRackFragment,
	SeoFragment,
	VendorFragment,
} from '../src/model/fragments'
import {
	DEFAULT_LOCALE,
	getLocale,
	getPossibleLocales,
	Locale,
} from '../src/model/locale'
import { axiosGet } from '../src/utils/axiosWrapper'
import { useTranslate } from '../src/hooks/useTranslate'
import { useIsInModal } from '../components/ModalManager'
import { Seo } from '../components/Seo'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Intro } from '../components/Intro'
import { WidthBound } from '../components/WidthBound'
import { Spacer } from '../components/Spacer'
import { Contained } from '../components/Contained'
import { ProductList, ProductListKind } from '../components/ProductList'
import { ProductRackMap } from '../components/ProductRackMap'
import { Pills } from '../components/Pills'
import { Content } from '../components/Content'

export default function ProductRack({
	productRack,
	possibleLocales,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const translate = useTranslate()
	const isInModal = useIsInModal()

	if (!productRack) {
		return <MissingContent />
	}

	const data = getLocale(productRack.locales, possibleLocales)

	if (!data) {
		return <MissingContent />
	}

	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					{
						label: data.title,
					}
				]}
			/>
			<WidthBound>
				<Intro title={data.title} subtitle={<strong>{translate('productRacks-label')} ({productRack.code})`</strong>} image={productRack.image}>
					<Content content={data.content} innerSize="narrow" viewInsideIntro />
				</Intro>
			</WidthBound>
			<Spacer initial={20}  />
			<WidthBound>
				<Pills pills={[
					{
						label: translate('vendors'),
						anchor: 'vendors'
					},
					{
						label: translate('variants'),
						anchor: 'variants'
					}
				]} />
				<Spacer initial={10}  />
			</WidthBound>
			<Contained>
				<WidthBound>
					<h3 id="vendors">{ translate('vendors-with-rack') }&nbsp;<span className='number-circle'>{ productRack.vendors.length }</span></h3>
					<ProductRackMap preload racks={[productRack]} />
					<Spacer initial={60}  />

					<h3 id="variants">{ translate('variants-in-rack') }&nbsp;<span className='number-circle'>{ productRack.products.length }</span></h3>
					<ProductList items={productRack.products} kind={ProductListKind.Generic} />
					<Spacer initial={60}  />
					
				</WidthBound>
			</Contained>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = (ctx.query.locale || DEFAULT_LOCALE) as Locale
	const possibleLocales = getPossibleLocales(locale)

	const { getProductRack: productRack } = await axiosGet('/racks/detail', {locale: locale, rackId: ctx.query.id}) as 
	{
		getProductRack: ProductRackFragment & {
			vendors: VendorFragment[],
			products: ProductFragment[],
			locales: (ProductRackFragment['locales'][0] & {
				seo: SeoFragment,
				content: ContentFragment
			})[]
		} 
	};

	return {
		props: { productRack, possibleLocales /*, modal: true, modalWidth: 'normal'*/ },
	}
}
